import type ApiRequestError from 'src/core/models/api/ApiRequestError';
import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View, StatusBar as ReactNativeStatusBar } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import ErrorView from 'src/presentation/components/organisms/ErrorView';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import AccountButton from 'src/presentation/components/molecules/AccountButton';
import PageHeader, { COMPACT_PAGE_HEADER_HEIGHT } from 'src/presentation/components/organisms/PageHeader';
import StickyHeader from 'src/presentation/components/molecules/StickyHeader';
import { useHomeStore } from 'src/domain/stores/useHomeStore';
import { useMessagesStore } from 'src/domain/stores/useMessagesStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import FeaturedItemsSlider from 'src/presentation/components/organisms/FeaturedItemsSlider';
import ExerciseOfTheDayCard from 'src/presentation/components/organisms/ExerciseOfTheDayCard';
import FeaturedItemDialog from 'src/presentation/dialogs/FeaturedItemDialog';
import LoyaltyStatusCard from 'src/presentation/components/organisms/LoyaltyStatusCard';
import { StatusBar } from 'expo-status-bar';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import BathCardLinkOrBuyCard from 'src/presentation/components/organisms/BathCardLinkOrBuyCard';
import BathCardsSlider from 'src/presentation/components/organisms/BathCardsSlider';
import PromotionCard from 'src/presentation/components/organisms/PromotionCard';
import ReserveVisitCard from 'src/presentation/components/organisms/ReserveVisitCard';
import RemoveBathCardDialog from 'src/presentation/dialogs/RemoveBathCardDialog';
import { BathCardVariant } from 'src/domain/models/BathCardVariant';
import { LoyaltyStatusVariant } from 'src/domain/models/LoyaltyStatusVariant';
import { PageHeaderVariant } from 'src/domain/models/PageHeaderVariant';
import { useBathCardsStore } from 'src/domain/stores/useBathCardsStore';
import { useLoyaltyStore } from 'src/domain/stores/useLoyaltyStore';
import useBathCardActions from 'src/presentation/hooks/useBathCardActions';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import Translations from 'src/core/utils/Translations';
import type { BottomTabNavigation } from 'src/presentation/navigation/types/NavigationTypes';

export default function HomeScreen() {
	const tabBarInset = useTabBarInset();
	const bottomTabNavigation = useNavigation<BottomTabNavigationProp<BottomTabNavigation>>();

	const [isHeaderSolid, setIsHeaderSolid] = useState(false);
	const onHeaderSolidChange = useCallback((isSolid: boolean) => setIsHeaderSolid(isSolid), []);

	const scrollY = useSharedValue(0);
	const onScroll = useAnimatedScrollHandler({
		onScroll: (e) => {
			scrollY.value = e.contentOffset.y;
		},
	});

	const { getApiMessages } = useMessagesStore();
	const { loyalty, errorMessage: loyaltyErrorMessage, getApiLoyalty } = useLoyaltyStore();
	const { bathCards, cardsPage, getApiBathCards, openRemoveCardDialog, setCardsPage } = useBathCardsStore();
	const { openBathCardPurchase, openReservationWithBathCard } = useBathCardActions();
	const { welcome, isLoading, hasError, featuredPage, getApiWelcome, setFeaturedPage } = useHomeStore();
	const { captureEvent } = useServicesStore();

	const linkedBathCards = bathCards ?? [];
	// /my/bath-cards syncs every card with Snowflake and lands well after the welcome call, so the count
	// welcome carries holds the block's place until the cards themselves are there.
	const expectedBathCards = bathCards === null ? (welcome?.data.bath_cards_count ?? 0) : linkedBathCards.length;
	const featuredTiles = welcome?.data.featured ?? [];
	const hasLoadedData = welcome !== null;

	const fetchData = async () => {
		await Promise.all([
			getApiWelcome(),
			getApiBathCards(),
			getApiLoyalty().catch((error: ApiRequestError) =>
				captureEvent({ message: 'getApiLoyalty failed', extra: { error } }),
			),
			getApiMessages().catch((error: ApiRequestError) =>
				captureEvent({ message: 'getApiMessages failed', extra: { error } }),
			),
		]);
	};

	const openLoyaltyTab = () =>
		bottomTabNavigation.navigate(Constants.screenTabLoyalty, { screen: Constants.screenLoyalty });

	const openBathCardTab = () =>
		bottomTabNavigation.navigate(Constants.screenTabBathCard, { screen: Constants.screenBathCardsList });

	const openBooking = () =>
		bottomTabNavigation.navigate(Constants.screenTabBooking, { screen: Constants.screenReserve });

	useAppStateScreenListener({
		onFocussed: () => fetchData(),
		onForegrounded: () => fetchData(),
	});

	const isFocused = useIsFocused();

	// The hero image sits behind the status bar, so the icons only stay light while it is in view.
	const hasLightHeader = hasLoadedData && !isHeaderSolid;

	useEffect(() => {
		if (isFocused) {
			ReactNativeStatusBar.setBarStyle(hasLightHeader ? 'light-content' : 'dark-content');
		}
	}, [isFocused, hasLightHeader]);

	return (
		<>
			<StatusBar style={isLoading || !hasLightHeader ? 'dark' : 'light'} />
			<View style={styles.mainWrapper}>
				<BackgroundPattern />
				<RemoveBathCardDialog />
				<FeaturedItemDialog />

				<StickyHeader
					headerRight={<AccountButton onImage={true} />}
					maxHeight={COMPACT_PAGE_HEADER_HEIGHT}
					scrollY={scrollY}
					title={welcome?.data.hero?.greeting}
					onSolidChange={onHeaderSolidChange}
				/>

				{isLoading ? (
					<LoadingIndicator style={{ flex: Dimens.flex1 }} />
				) : hasError ? (
					<ErrorView onPress={fetchData} />
				) : (
					<Animated.ScrollView
						contentContainerStyle={{ paddingBottom: tabBarInset + Dimens.padding16, gap: Dimens.gap16 }}
						scrollEventThrottle={16}
						showsVerticalScrollIndicator={false}
						onScroll={onScroll}
					>
						{welcome ? (
							<PageHeader
								image={{ uri: welcome.data.hero.header_image }}
								title={welcome.data.hero?.greeting}
								variant={PageHeaderVariant.Compact}
							/>
						) : null}

						{expectedBathCards > 0 ? (
							<BathCardsSlider
								cards={linkedBathCards}
								page={cardsPage}
								setPage={setCardsPage}
								showHeader={expectedBathCards > 1}
								skeletonCount={expectedBathCards}
								variant={BathCardVariant.Summary}
								onPressBuy={() => openBathCardPurchase()}
								onPressRemove={(card) => openRemoveCardDialog(card)}
								onPressReserve={(card) => openReservationWithBathCard(card)}
								onPressView={() => openBathCardTab()}
							/>
						) : null}

						{/* Keep the last known status when a refresh fails; hide the card only when the first load errored. */}
						{loyalty?.status || loyaltyErrorMessage === null ? (
							<LoyaltyStatusCard
								status={loyalty?.status}
								style={styles.loyaltyStatusCard}
								variant={LoyaltyStatusVariant.Summary}
								onPressBenefits={() => openLoyaltyTab()}
							/>
						) : null}

						<PromotionCard promotion={welcome?.data.promotion ?? null} style={styles.promotionCard} />

						<ExerciseOfTheDayCard
							content={welcome?.data.exercise_of_the_day ?? null}
							style={styles.exerciseCard}
							variant="light"
						/>

						{welcome !== null && expectedBathCards === 0 ? (
							<>
								<ReserveVisitCard style={styles.bathCardsBlock} onPress={() => openBooking()} />
								<BathCardLinkOrBuyCard
									heading={Translations.bathcardNoCardHeading}
									style={styles.bathCardsBlock}
									onPressBuy={() => openBathCardPurchase()}
									onPressLink={() => openBathCardTab()}
								/>
							</>
						) : null}

						<FeaturedItemsSlider items={featuredTiles} page={featuredPage} setPage={setFeaturedPage} />
					</Animated.ScrollView>
				)}
			</View>
		</>
	);
}

const styles = StyleSheet.create({
	mainWrapper: {
		flex: Dimens.flex1,
	},
	promotionCard: {
		marginHorizontal: Dimens.padding18,
	},
	exerciseCard: {
		marginHorizontal: Dimens.padding18,
	},
	loyaltyStatusCard: {
		marginHorizontal: Dimens.padding18,
	},
	bathCardsBlock: {
		marginHorizontal: Dimens.padding18,
	},
});
